/**
 * Release manifest - the single source of truth for "what changed".
 *
 * The data lives in `releases.json` next to this file. The running bundle
 * imports it, so it carries the version it was built from, while a Vite plugin
 * publishes the same JSON as `/version.json` at the site root, which the server
 * always serves fresh. A tab open across a deploy therefore holds an old
 * constant and reads a new file, and that difference is what makes a stale tab
 * detectable at all.
 *
 * HOW TO PUBLISH A RELEASE
 *   1. Add an entry at the TOP of `releases` in releases.json (newest first).
 *   2. Bump `version` using semver.
 *   3. Set `mandatory` to true only when an old bundle genuinely cannot keep
 *      working: a breaking API change, a corrected calculation, a security fix.
 *      A mandatory release interrupts everyone with a countdown and then
 *      reloads them, so it is not the default.
 *   4. Deploy. Open tabs notice within the poll interval.
 *
 * Nothing here is user-specific, so it is safe to serve publicly.
 */

import manifest from './releases.json';

/** How a single line item reads in the "What's new" modal. */
export type ReleaseChangeKind = 'added' | 'improved' | 'fixed';

export interface ReleaseChange {
  kind: ReleaseChangeKind;
  text: string;
}

export interface Release {
  /** Semver, e.g. "1.1.0". Compared numerically, never as a string. */
  version: string;
  /** ISO date (yyyy-mm-dd) the release went out. */
  date: string;
  /** Short headline for the modal, e.g. "Scheduled tasks". */
  title: string;
  /**
   * Whether a tab still running an older bundle must be reloaded. Any pending
   * release carrying this makes the whole update mandatory, so someone who
   * skipped three optional releases is still forced by the fourth.
   */
  mandatory?: boolean;
  changes: ReleaseChange[];
}

/** Newest first. The first entry is what a fresh build reports as current. */
export const RELEASES = manifest.releases as Release[];

/**
 * The version this bundle was built from. Because it comes from the same file
 * the published manifest is generated out of, it cannot drift from what shipped.
 */
export const CURRENT_VERSION = RELEASES[0]?.version ?? '0.0.0';

/**
 * Compares two semver strings numerically. "1.10.0" is newer than "1.9.0",
 * which a plain string comparison gets wrong.
 */
export function isNewerVersion(candidate: string, baseline: string): boolean {
  const parse = (v: string) => v.split('.').map((n) => Number.parseInt(n, 10) || 0);
  const a = parse(candidate);
  const b = parse(baseline);

  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) return diff > 0;
  }
  return false;
}

/** localStorage key backing the dev-only version override. */
const PRETEND_VERSION_KEY = 'ccms:pretendVersion';

/**
 * The version the update check should treat this bundle as running.
 *
 * Normally CURRENT_VERSION. In development only, it can be overridden so the
 * update flow can actually be exercised: the bundle and the published manifest
 * are generated from the same file, so a freshly built app is never behind
 * itself and the notice would otherwise be impossible to see locally.
 *
 *   ?pretendVersion=1.0.0   pretend this bundle is older, and see the notice
 *   ?pretendVersion=        clear the override
 *
 * The value is remembered in localStorage so it survives navigation, and the
 * query string can then be dropped.
 *
 * Deliberately DEV-only. Honouring it in production would let a client claim to
 * be newer than it is and quietly opt itself out of a mandatory update, which
 * is the one thing a forced update must not allow.
 */
export function getRunningVersion(): string {
  if (!import.meta.env.DEV || typeof window === 'undefined') return CURRENT_VERSION;

  try {
    const param = new URLSearchParams(window.location.search).get('pretendVersion');

    if (param !== null) {
      if (param === '') {
        window.localStorage.removeItem(PRETEND_VERSION_KEY);
        return CURRENT_VERSION;
      }
      window.localStorage.setItem(PRETEND_VERSION_KEY, param);
      return param;
    }

    return window.localStorage.getItem(PRETEND_VERSION_KEY) || CURRENT_VERSION;
  } catch {
    // Private browsing can throw on storage access; fall back to the real one.
    return CURRENT_VERSION;
  }
}

/**
 * Clears the dev-only override.
 *
 * Called just before a reload triggered by the update notice. Without it the
 * override survives into the fresh page, which sees itself as behind again and
 * shows the same required update, so a mandatory test reloads forever. The
 * override exists to see the flow once, not to be stuck in it: re-add
 * ?pretendVersion=... to run through it again.
 */
export function clearPretendVersion(): void {
  if (!import.meta.env.DEV || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PRETEND_VERSION_KEY);
  } catch {
    // Storage unavailable; nothing to clear.
  }
}
